import React, { useState, useEffect, useCallback } from "react";
import moment from 'moment-timezone';
import i18next from "i18next";

import { DevicesService, AssetsService, DeviceDataOverrideService } from '../../services';
import { GraphDataConverter } from '../../helpers';
import { LineGraph, DoorAreaGraph, StateAreaGraph } from '../Graphs';

import config from "config";

const AssetDetailReportGraphs = ({query, initCharts, initHideExport, dataFetched}) => {
  const grouping = query.location?.grouping;
  
  const convertTimestamp = (timestamp, grouping, start) => {
    const DATE_FORMAT = "L";
    if (grouping?.timezone) {
        const offset = moment(timestamp, DATE_FORMAT).tz(grouping?.timezone).utcOffset();
        return start ? (moment(timestamp, DATE_FORMAT).utc().startOf("day").valueOf() - offset * 60 * 1000) : (moment(timestamp, DATE_FORMAT).utc().endOf("day").valueOf() - offset * 60 * 1000);
    } else {
        return start ? moment(timestamp, DATE_FORMAT).startOf("day").valueOf() : moment(timestamp, DATE_FORMAT).endOf("day").valueOf();
    }
  };

  const [runningReport, setRunningReport] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [charts, setCharts] = useState(initCharts);
  const [hideExport, setHideExport] = useState(initHideExport);
  const [startAtDate, setStartAtDate] = useState(query.startAtDate ? convertTimestamp(query.startAtDate, grouping, true) : null);
  const [endAtDate, setEndAtDate] = useState(query.endAtDate ? convertTimestamp(query.endAtDate, grouping, false) : null);

  const handleDeviceResponse = (response) => {
      const primaryDataTypes = response.detailReportData.deviceDataTypeList.sort((a, b) => a.displayOrder - b.displayOrder);
      let updatedCharts = [];

      if (primaryDataTypes.length > 0) {
          primaryDataTypes.forEach((primaryDataType) => {
              let displayData = response.detailReportData.allDeviceData["" + primaryDataType.id];

              if (displayData.readings && displayData.readings.length > 0) {
                  let label = i18next.t([`device-data-display-types.${DeviceDataOverrideService.getOverrideForDisplayToken(primaryDataType.token)}`, `device-data-types.${primaryDataType.token}`]);
                  var type = "line";
                  if (primaryDataType.token === "magnetometer" || primaryDataType.token === "door_open") {
                      type = "magnetometer";
                  } else if (GraphDataConverter.hasStateValue(displayData.readings)) {
                      type = primaryDataType.token;
                  }
                  let timeframeInSeconds = Math.round((endAtDate - startAtDate) / 1000);
                  let data = GraphDataConverter.convertReadingDataToGraphData(primaryDataType.token, displayData.timezone, displayData.readings, endAtDate, timeframeInSeconds);

                  let outagesAtStartOrEnd = GraphDataConverter.convertOutagesToGraphData(displayData.outagesAtStartOrEnd, displayData.timezone);

                  updatedCharts.push({ data: data, label: label, type: type, timeframe: timeframeInSeconds, outagesAtStartOrEnd: outagesAtStartOrEnd });
              }
          });

          setCharts(updatedCharts);
          setHideExport(false);
          setRunningReport(false);

          dataFetched(updatedCharts);
      }
  };

  const handleAssetResponse = (response) => {
      let updatedCharts = [];

      if (response.detailReportData.assetMeasures.length > 0) {
          response.detailReportData.assetMeasures.forEach((assetMeasure) => {
              let displayData = response.detailReportData.allDeviceData["" + assetMeasure.id];

              if (displayData.readings && displayData.readings.length > 0) {
                  var type = "line";
                  if (assetMeasure.deviceDataType.token === "magnetometer" || assetMeasure.deviceDataType.token === "door_open") {
                      type = "magnetometer";
                  } else if (GraphDataConverter.hasStateValue(displayData.readings)) {
                      type = assetMeasure.deviceDataType.token;
                  }
                  let timeframeInSeconds = Math.round((endAtDate - startAtDate) / 1000);
                  let data = GraphDataConverter.convertReadingDataToGraphData(assetMeasure.deviceDataType.token, displayData.timezone, displayData.readings, endAtDate, timeframeInSeconds);

                  let outagesAtStartOrEnd = GraphDataConverter.convertOutagesToGraphData(displayData.outagesAtStartOrEnd, displayData.timezone);

                  updatedCharts.push({ data: data, label: assetMeasure.assetTypeDeviceDataType.name, type: type, timeframe: timeframeInSeconds, outagesAtStartOrEnd: outagesAtStartOrEnd });
              }
          });

          setCharts(updatedCharts);
          setHideExport(false);
          setRunningReport(false);

          dataFetched(updatedCharts);
      }
  };

  useEffect(() => {
    if (!initCharts) {
      setRunningReport(true);

      const localStartAtDate = convertTimestamp(query.startAtDate, grouping, true);
      const localEndAtDate = convertTimestamp(query.endAtDate, grouping, false);

      setStartAtDate(localStartAtDate);
      setEndAtDate(localEndAtDate);

      if (query.entity.entityTypeToken == 'device') {
        DevicesService.getDeviceDetailReport(localStartAtDate, localEndAtDate, query.entity.value).then(handleDeviceResponse).catch((error) => {
            console.error(error);
            // TODO handle error
        });
      } else {
        AssetsService.getAssetDetailReport(localStartAtDate, localEndAtDate, query.entity.value).then(handleAssetResponse).catch((error) => {
            console.error(error);
            // TODO handle error
        });
      }
    }
  }, [query, initCharts, hideExport]);

  const exportData = useCallback(() => {
    setExportLoading(true);

    if (query.entity.entityTypeToken == 'device') {
      DevicesService.getDeviceDetailReportCSV(startAtDate, endAtDate, query.entity.value)
        .then(() => setExportLoading(false))
        .catch((error) => {
          console.error(error);
          setExportLoading(false);
          // TODO handle error
        });
    } else {
      AssetsService.getAssetDetailReportCSV(startAtDate, endAtDate, query.entity.value)
        .then(() => setExportLoading(false))
        .catch((error) => {
          console.error(error);
          setExportLoading(false);
          // TODO handle error
        });
    }
  }, [startAtDate, endAtDate, query]);

  if (grouping && !runningReport) {
    return (
        <div className="animated fadeIn">
          <div className="op-content-box">
            <div className="l-region__heading">
              <h2 style={{fontFamily: 'roboto'}}> <i>{ i18next.t('pages.asset-detail-report.readings-from') }</i><b> {query.entity.label}</b> </h2>
            </div>
            <div className="device-inner device-content-box">
              {
                hideExport ? null : exportLoading ? <a className ="pull-right tooltip"><img id="export-icon" className="spinner" src={`${config.WEB_BASE_URL}/images/icon/themes/${window.globalBrand}/export_icon.svg`} onClick={exportData} alt={ i18next.t('table-headers.export-data') } /> <span className="tooltiptext">{ i18next.t('table-headers.export-data') }</span></a> : <a className ="pull-right tooltip"><img id="export-icon" src={`${config.WEB_BASE_URL}/images/icon/themes/${window.globalBrand}/export_icon.svg`} onClick={exportData} alt={ i18next.t('table-headers.export-data') } /> <span className="tooltiptext">{ i18next.t('table-headers.export-data') }</span></a>
              }
              {
                (charts?.length > 0) ? charts?.map((chart, index) => 
                      <div key={index} className="text-center">
                        { 
                          chart.type === "magnetometer" ? 
                            <DoorAreaGraph graphVitals={chart} showBrush={true} width={850} height={600} id={0} /> : 
                            chart.type === "line" ? 
                            <LineGraph graphVitals={chart} showBrush={true} width={850} height={600} /> :
                            <StateAreaGraph dataTypeToken={chart.type} graphVitals={chart} showBrush={true} width={850} height={600} id={0} />
                        }
                      </div>) 
                      :
                      <div className="text-center" style={{height: 550, padding: 0}}>
                        <p>{ i18next.t('messages.no-data-to-display') }</p>
                      </div>
              }
            </div>
          </div>
        </div>
    );
  } else {
    return (<div className="loader">{ i18next.t('loading') }</div>);
  }
};

export default AssetDetailReportGraphs;
